"use client";

import styles from "./Leaderboard.module.scss";
import FontAwesome from "../../utilities/FontAwesome";
import classNames from "classnames";
import ClickableLink from "../../utilities/ClickableLink";
import { useStores } from "../../../stores/useStores";
import { Suspense, use, useEffect } from "react";
import { IView } from "../../../stores/InterfaceStore";
import { LeaderboardEntry } from "../SchoolFeedDisplay";

// MC.openUserReport reads from MC_Account._accounts, an in-memory cache populated
// only when the MC server sends an `accounts` field on a WS message. Ask MC for the
// leaderboard so it pushes those accounts back; without this the modal renders an
// empty/anonymous user. Relies on another MeritChat component on the page to have
// loaded meritchat.js and opened the WebSocket.
function primeMeritChatAccountCache(suborganizationId: string) {
  if (typeof window === "undefined") return;

  const send = (attempt = 0) => {
    if (
      typeof MC !== "undefined" &&
      MC.ws &&
      MC.ws.readyState === WebSocket.OPEN &&
      MC.accountID !== undefined
    ) {
      MC.ws.send(
        JSON.stringify({
          type: "leaderboard.get",
          suborganization: suborganizationId,
        }),
      );
      return;
    }
    if (attempt > 50) return;
    setTimeout(() => send(attempt + 1), 200);
  };

  send();
}

function LeaderboardMembersList(props: {
  dataPromise: Promise<LeaderboardEntry[]>;
  organizationId: string;
  isAdmin: boolean;
}) {
  const data = use(props.dataPromise);

  const handleClick = (accountId: string) => {
    if (typeof MC !== "undefined") {
      MC.openUserReport(accountId, props.organizationId);
    }
  };

  return (
    <ol>
      {data.map((d, idx) => {
        const itemClassName = classNames({
          [styles.item]: true,
          [styles.itemIsMe]: d["me"],
        });
        const content = (
          <>
            <span>
              {d.rank} - {d.name}
            </span>
            <span className={styles.score}>{d.score} XP</span>
          </>
        );
        return (
          <li key={idx} className={itemClassName}>
            {props.isAdmin ? (
              <button
                type="button"
                className={styles.itemButton}
                onClick={() => handleClick(d.id)}
              >
                {content}
              </button>
            ) : (
              <div className={styles.itemContent}>{content}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export default function Leaderboard(props: {
  leaderboardDataPromise: Promise<LeaderboardEntry[]>;
}) {
  const { interfaceStore, organizationStore, userStore } = useStores();

  useEffect(() => {
    if (!userStore.admin) return;
    primeMeritChatAccountCache(organizationStore.currentOrganization.id);
  }, [userStore.admin, organizationStore.currentOrganization.id]);

  return (
    <div className={styles.container}>
      <div>
        <h3>DISCUSSION LEADERBOARD</h3>
        {interfaceStore.view !== IView.LEADERBOARD && (
          <ClickableLink href={"/leaderboard"}>
            Learn More{" "}
            <FontAwesome prefix={"far"} name={"fa-question-circle"} />
          </ClickableLink>
        )}
      </div>
      <Suspense>
        <LeaderboardMembersList
          dataPromise={props.leaderboardDataPromise}
          organizationId={organizationStore.currentOrganization.id}
          isAdmin={userStore.admin}
        />
      </Suspense>
    </div>
  );
}
